import { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableRow,
    List,
    ListItem,
    Divider,
    Button,
    IconButton,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { Previewer } from '../../../../../../../components/DocumentPreviewer/Previewer';
import { generateSignedDocumentUrl } from '../../api';

const Step7 = ({ lease = {} }) => {
    const { attached_documents = [], id: leaseId } = lease;
    const [error, setError] = useState(null);
    const [signedUrls, setSignedUrls] = useState({});

    // Helper function to get initials from name
    const getInitials = (name) => {
        if (!name || typeof name !== 'string') return 'N/A';
        return name
            .trim()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase())
            .join('');
    };

    const handleDownload = async (documentId, documentName, signatures, fileUrl, signedUrl) => {
        try {
            setError(null);

            const hasSignatures = signatures &&
                signatures.length > 0 &&
                signatures.some(sig => sig.signature && sig.signature.trim() !== '');

            let downloadUrl;

            if (hasSignatures) {
                const response = await generateSignedDocumentUrl(leaseId, documentId, signatures);
                console.log({ response });

                if (response) {
                    downloadUrl = response.data.signedUrl;
                } else {
                    setError('Failed to generate signed URL');
                    return;
                }
            } else {
                downloadUrl = signedUrl || fileUrl;
            }

            if (downloadUrl) {
                const response = await fetch(downloadUrl, { mode: 'cors' });
                if (!response.ok) throw new Error('Network response was not ok');

                const blob = await response.blob();
                const blobUrl = window.URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = documentName || 'document';
                document.body.appendChild(link);
                link.click();

                // Cleanup
                link.remove();
                window.URL.revokeObjectURL(blobUrl);
            }
            else {
                setError('No valid URL available for download');
            }
        } catch (error) {
            console.error('Error downloading document:', error);
            setError('Failed to download document');
        }
    };

    const getFileNameFromUrl = (url, fallbackName) => {
        try {
            const urlParts = url.split('/');
            const filename = urlParts[urlParts.length - 1];
            const cleanFilename = filename.split('?')[0];
            return decodeURIComponent(cleanFilename) || fallbackName || 'document';
        } catch (error) {
            return fallbackName || 'document';
        }
    };

    const Footer = ({ document, lease }) => {
        const landlordName = lease?.manager
            ? `${lease.manager.first_name || ''} ${lease.manager.last_name || ''}`.trim()
            : '';
        const landlordInitials = getInitials(landlordName);

        const tenantInitials = lease?.tenants?.length > 0
            ? lease.tenants
                .map(tenant => (tenant.initials && tenant.initials.trim()) || getInitials(tenant.name))
                .filter(initial => initial !== 'N/A')
                .join(', ') || 'N/A'
            : 'N/A';

        const hasSignatures = document.signatures &&
            document.signatures.length > 0 &&
            document.signatures.some(sig => sig.signature && sig.signature.trim() !== '');

        return (
            <Box
                className="lease-form-footer"
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mt: 2,
                    borderTop: '1px solid',
                    borderColor: 'grey.300',
                    pt: 2,
                }}
            >
                <Box sx={{ flex: 1 }}>
                    {hasSignatures && (
                        <List dense>
                            {document.signatures
                                .filter(signature => signature.signature && signature.signature.trim() !== '')
                                .map((signature, sigIndex) => (
                                    <ListItem key={sigIndex} sx={{ py: 0.25, px: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <img
                                                src={signature.signature}
                                                alt="Signature"
                                                style={{
                                                    maxWidth: '80px',
                                                    maxHeight: '15px',
                                                    objectFit: 'contain'
                                                }}
                                            />
                                            <Typography
                                                variant="caption"
                                                sx={{ fontSize: '0.4rem', color: 'text.secondary' }}
                                            >
                                                {signature.date && signature.date.trim() !== ''
                                                    ? signature.date
                                                    : 'Date not provided'
                                                }
                                            </Typography>
                                        </Box>
                                    </ListItem>
                                ))
                            }
                        </List>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box textAlign="center" mr={2}>
                        <Typography variant="caption" fontSize="0.7rem">
                            INITIALS
                        </Typography>
                    </Box>
                    <Table
                        size="small"
                        sx={{
                            width: '120px',
                            border: '1px solid',
                            borderColor: 'grey.500'
                        }}
                    >
                        <TableBody>
                            <TableRow>
                                <TableCell
                                    align="center"
                                    sx={{
                                        borderRight: '1px solid',
                                        borderColor: 'grey.500',
                                        fontSize: '0.8rem',
                                        p: 0.5
                                    }}
                                >
                                    Landlord
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{ fontSize: '0.8rem', p: 0.5 }}
                                >
                                    Tenant(s)
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell
                                    align="center"
                                    sx={{
                                        borderRight: '1px solid',
                                        borderColor: 'grey.500',
                                        fontSize: '0.9rem',
                                        p: 0.5
                                    }}
                                >
                                    {landlordInitials}
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{ fontSize: '0.9rem', p: 0.5 }}
                                >
                                    {tenantInitials}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Box>
            </Box>
        );
    };

    const DocumentFileHeader = ({ url, urlIndex, documentName, totalFiles, documentId, signatures }) => {
        const fileName = getFileNameFromUrl(url.file_url, documentName);
        const displayName = totalFiles > 1
            ? `${documentName} - File ${urlIndex + 1}`
            : documentName;

        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                    p: 1,
                    bgcolor: 'grey.50',
                    borderRadius: 1
                }}
            >
                <Box>
                    <Typography variant="subtitle2">
                        {displayName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {fileName}
                    </Typography>
                </Box>
                <IconButton
                    onClick={() => handleDownload(documentId, fileName, signatures, url.file_url, url.signed_url)}
                    disabled={!url.file_url || url.file_url === 'upload_requested'}
                    size="small"
                    sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'primary.dark' },
                        '&:disabled': { bgcolor: 'grey.300', color: 'grey.500' }
                    }}
                >
                    <DownloadIcon fontSize="small" />
                </IconButton>
            </Box>
        );
    };

    const DocumentFilePreview = ({ url, documentId }) => {
        const signedUrl = signedUrls[documentId] || url.signed_url;

        if (error) {
            return (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '500px'
                }}>
                    <Typography color="error">{error}</Typography>
                </Box>
            );
        }

        if (!url.file_url || url.file_url === 'upload_requested') {
            return (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '500px',
                    border: '1px solid',
                    borderColor: 'grey.300',
                    borderRadius: 1,
                }}>
                    <Typography color="text.secondary">
                        Document preview not available.
                    </Typography>
                </Box>
            );
        }

        return (
            <Box
                sx={{
                    mb: 2,
                    minHeight: '700px',
                    border: '1px solid',
                    borderColor: 'grey.300',
                    borderRadius: 1,
                    overflow: 'hidden',
                }}
            >
                <Previewer
                    url={url.file_url}
                    signedUrl={signedUrl}
                    width="100%"
                    height="90vh"
                />
            </Box>
        );
    };

    return (
        <Box className="lease-form-step7">
            <Box className="documents">
                <Typography variant="h6" align="left" gutterBottom className="notice">
                    DOCUMENTS AND SIGNATURES
                </Typography>

                <Typography variant="body2" align="left" className="notice-desc" sx={{ mb: 2 }}>
                    The following documents are part of your lease agreement.
                </Typography>

                <Box className="documents-details-table" sx={{ my: 2 }}>
                    <Paper variant="outlined" sx={{ mb: 2, borderRadius: 1, p: 3 }}>
                        {attached_documents.length > 0 ? (
                            attached_documents.map((document, documentIndex) => (
                                <Box key={documentIndex}>
                                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                                        {document.name || `Document ${documentIndex + 1}`}
                                    </Typography>

                                    {document.urls && document.urls.length > 0 ? (
                                        document.urls.map((url, urlIndex) => (
                                            <Box key={`${documentIndex}-${urlIndex}`}>
                                                <Box
                                                    sx={{
                                                        mb: 4,
                                                        pb: 3,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        minHeight: '100%',
                                                    }}
                                                >
                                                    <DocumentFileHeader
                                                        url={url}
                                                        urlIndex={urlIndex}
                                                        documentName={document.name}
                                                        totalFiles={document.urls.length}
                                                        documentId={document.id}
                                                        signatures={document.signatures || []}
                                                    />

                                                    <Box
                                                        sx={{
                                                            mt: 2,
                                                            flexGrow: 1,
                                                            minHeight: '500px',
                                                            position: 'relative',
                                                        }}
                                                    >
                                                        <DocumentFilePreview url={url} documentId={document.id} />
                                                    </Box>

                                                    <Box sx={{ mt: 'auto' }}>
                                                        <Footer document={document} lease={lease} />
                                                    </Box>
                                                </Box>

                                                {urlIndex < document.urls.length - 1 && (
                                                    <Divider sx={{ my: 3, borderColor: 'lightgray', borderStyle: 'dashed' }} />
                                                )}
                                            </Box>
                                        ))
                                    ) : (
                                        <Box sx={{ p: 2, textAlign: 'center', color: '#aaa' }}>
                                            No files available for this document.
                                        </Box>
                                    )}

                                    {documentIndex < attached_documents.length - 1 && (
                                        <Divider sx={{ my: 6, borderColor: 'gray' }} />
                                    )}
                                </Box>
                            ))
                        ) : (
                            <Box sx={{ p: 2, textAlign: 'center', color: '#aaa' }}>
                                No Documents added yet.
                            </Box>
                        )}
                    </Paper>
                </Box>
            </Box>
        </Box>
    );
};

export { Step7 };